'use client';

import React from 'react';

import { cn } from '@lib';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { IconChevronDown } from '@tabler/icons-react';

import { Badge } from './badge';

const AccordionRoot = AccordionPrimitive.Root;

export type AccordionProps = React.ComponentProps<typeof AccordionRoot> & {
  items: {
    title: string;
    content: React.ReactNode;
    badge?: string;
  }[];
  defaultOpen?: boolean;
  type: 'single' | 'multiple';
};

const Accordion = ({ type = 'single', defaultOpen, items, ...rest }: AccordionProps) => (
  <AccordionRoot
    className="z-10 w-full max-w-3xl"
    type={type}
    collapsible="true"
    defaultValue={defaultOpen ? items?.map(({ title }) => title) : undefined}
    {...(rest as any)}
  >
    {items.map((item) => (
      <AccordionItem key={item.title} value={item.title}>
        <AccordionTrigger>
          {!item.badge && item.title}
          {item.badge && (
            <div className="flex grow items-baseline justify-between">
              <span className={'grow'}>{item.title}</span>
              <Badge variant={'secondary'}>{item.badge}</Badge>
            </div>
          )}
        </AccordionTrigger>
        <AccordionContent>{item?.content}</AccordionContent>
      </AccordionItem>
    ))}
  </AccordionRoot>
);

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn('border-border border-b', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        '[&[data-state=closed]>svg]:text-muted-foreground flex flex-1 items-center justify-between py-4 text-start font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <IconChevronDown className="h-4 w-4 shrink-0 transition duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden"
    {...props}
  >
    <div className={cn('pt-0 pb-4', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
